import React, { useEffect, useState } from 'react';
import css from './AdminPageFlipAndWin.css';
import { useRequest } from '../../hooks/useRequest';
import { getFlipAndWinCards } from '../../util/api';
import { datediff } from '../../util/time';
import { Alert, IconSpinner, SortingBadge } from '../../components';
import { IconSearchM } from '../../icons';
import { createResourceLocatorString } from '../../util/routes';
import AdminHOC from '../AdminPanelsPage/AdminHOC';
import EditForm from './EditForm';
import InlineAdminEntity from './InlineAdminEntity';
import sortingOptions from './sortingOptions';
import FilterBadgesPanel from './FilterBadgesPanel';
import routeConfiguration from '../../routeConfiguration';
import { FiltersBadge as SecondaryFiltersPopup } from '../AdminPanelsPage/FiltersBadge';

const badgesTypeMapper = {
    credits: 'Credits',
    coupon: 'Coupon',
    product: 'Produkt',
};

const Panel = ({
    showList,
    handleAdminAction,
    handleSubmit,
    goBack,
    showForm,
    initialValues,
    action,
    history,
    retriggerKey,
    loading: parentLoading,
    ready,
}) => {
    const [cards, setCards] = useState([]);
    const [country, setCountry] = useState([]);
    const [targetUsersType, setTargetUsersType] = useState([]);
    const [countryType, setCountryType] = useState('all');
    const [searchValue, setSearchValue] = useState('');
    const [sortOption, setSortOption] = useState(sortingOptions[0]);

    const { error, loading, request } = useRequest();
    const requestCards = async () => await request(getFlipAndWinCards, setCards);

    useEffect(() => {
        requestCards();
    }, [retriggerKey]);

    const inProgress = loading || parentLoading;

    if (showList && inProgress) {
        return <IconSpinner />;
    }

    if (error) {
        return <Alert showAsPlainText type="failure" description={error} closeAllowed={false} />;
    }

    const filterByCountry = ({ countries }) => {
        return (
            !country ||
            country.length === 0 ||
            countries.length === 0 ||
            countries.some(c => country.includes(c))
        );
    };

    const filterByEndDate = ({ endDate }) => {
        if (!endDate?.date) return false;

        const { hours } = datediff(new Date(), new Date(endDate.date));
        return hours <= 1;
    };

    const filterRestCardsCriteria = entry => !filterByEndDate(entry) && !filterByNoInStock(entry);

    const filterByNoInStock = entry =>
        entry.hasOwnProperty('inStock') &&
        entry.hasOwnProperty('totalWins') &&
        entry.inStock - entry.totalWins < 1;

    const filterByUserInput = ({ title }) =>
        title.toLowerCase().includes(searchValue.toLowerCase());

    const filterByUserType = ({ userType }) =>
        !userType ||
        userType.length === 0 ||
        targetUsersType.length === 0 ||
        targetUsersType.some(c => userType.includes(c));

    const showLatest = countryType === 'latest';
    const showNoInStock = countryType === 'noInStock';
    const showRestCards = countryType === 'rest';

    const countryCards = cards.filter(filterByCountry);

    const targetUsersTypeCards = countryCards.filter(filterByUserType);
    const userInputCards = searchValue
        ? targetUsersTypeCards.filter(filterByUserInput)
        : targetUsersTypeCards;
    const latestCards = userInputCards.filter(filterByEndDate);
    const noInStock = userInputCards.filter(filterByNoInStock);
    const restCards = userInputCards.filter(filterRestCardsCriteria);

    const renderCards = () => {
        if (showRestCards)
            return restCards.sort(({ createdAt: ca1 }, { createdAt: ca2 }) => ca2 - ca1);

        if (showLatest) return latestCards;
        if (showNoInStock) return noInStock;

        return userInputCards;
    };

    const sortCards = (a, b) => {
        const { key } = sortOption;
        if (key === 'endDate') {
            const dateA = a?.endDate?.date ? new Date(a.endDate.date).getTime() : 0;
            const dateB = b?.endDate?.date ? new Date(b.endDate.date).getTime() : 0;

            return dateB - dateA;
        }
        if (key === 'lifetime') {
            const lifetimeA = (a?.inStock || 0) - (a.totalWins || 0);
            const lifetimeB = (b?.inStock || 0) - (b.totalWins || 0);

            return lifetimeB - lifetimeA;
        }
        if (key === 'createdAt') {
            return new Date(b[key]).getTime() - new Date(a[key]).getTime();
        }
        return (b[key] || 0) - (a[key] || 0);
    };

    const cardsToRender = renderCards().sort(sortCards);
    const noCardsToRender = showList && cardsToRender.length === 0;

    return (
        <>
            {showForm && (
                <p type="button" onClick={goBack} className={css.backButton}>
                    Zurück
                </p>
            )}
            {!showForm && (
                <header className={css.headerSection}>
                    <h1 className={css.pageTitle}>Flip & Win Console</h1>
                    <nav className={css.adminNavigation}>
                        <p className={css.badge} onClick={() => handleAdminAction('add')}>
                            admin::add
                        </p>
                        <p
                            className={css.badge}
                            onClick={() =>
                                history.push(
                                    createResourceLocatorString('FlipAndWin', routeConfiguration())
                                )
                            }
                        >
                            admin::watch::list
                        </p>
                    </nav>
                </header>
            )}
            {showList && (
                <div>
                    <nav className={css.adminNavBar}>
                        <FilterBadgesPanel
                            allCards={userInputCards}
                            restCards={restCards}
                            latestCards={latestCards}
                            noInStock={noInStock}
                            countryType={countryType}
                            setCountryType={setCountryType}
                        />

                        <section>
                            <div className={css.searchInput}>
                                <input
                                    placeholder="Suche"
                                    id="cards-search"
                                    value={searchValue}
                                    onChange={e => setSearchValue(e.target.value)}
                                />
                                <label htmlFor="cards-search">
                                    <IconSearchM />
                                </label>
                            </div>
                        </section>
                        <section className={css.filtersSection}>
                            <SecondaryFiltersPopup
                                optionsSelected={country.length + targetUsersType.length}
                                initialValues={{
                                    country,
                                    userType: targetUsersType,
                                }}
                                handleOptionUpdate={values => {
                                    const { country, userType } = values || {};
                                    setCountry(country || []);
                                    setTargetUsersType(userType || []);
                                }}
                            />
                            <SortingBadge
                                selectedOption={sortOption}
                                options={sortingOptions}
                                onSubmit={setSortOption}
                                rootClassName={css.sortingBadge}
                            />
                        </section>
                    </nav>

                    {noCardsToRender && (
                        <Alert header="No cards found." description="Cards have not been found." />
                    )}

                    <div className={css.offersHolder}>
                        {cardsToRender.map(card => {
                            const { probability, priority, type, monthPrize, id } = card;
                            return (
                                <InlineAdminEntity
                                    key={id}
                                    handleAdminAction={handleAdminAction}
                                    badges={[
                                        priority ? `Priorität ${priority}` : null,
                                        `${probability}% Chance`,
                                        `${badgesTypeMapper[type]}`,
                                        monthPrize ? `Gewinn des Monats` : null,
                                    ].filter(s => !!s)}
                                    entity={card}
                                />
                            );
                        })}
                    </div>
                </div>
            )}
            {showForm && (
                <EditForm
                    onSubmit={handleSubmit}
                    initialValues={initialValues}
                    action={action}
                    ready={ready}
                    inProgress={inProgress}
                />
            )}
        </>
    );
};

export default AdminHOC(Panel);
